import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Polls for active maintenance windows.
 *
 * @param {Object} options
 * @param {boolean} options.enabled  - whether polling is active
 * @param {number}  options.interval - polling interval in ms (default 30000)
 * @param {Object}  options.initialMaintenance - maintenance data from login response
 * @returns {{ isUnderMaintenance: boolean, message: string, endsAt: Date|null }}
 */
function useMaintenanceCheck({ enabled = true, interval = 30000, initialMaintenance = null } = {}) {
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(
    initialMaintenance?.active || false
  );
  const [message, setMessage] = useState(initialMaintenance?.message || '');
  const [endsAt, setEndsAt] = useState(
    initialMaintenance?.validTo ? new Date(initialMaintenance.validTo) : null
  );
  const intervalRef = useRef(null);
  const wasUnderMaintenanceRef = useRef(initialMaintenance?.active || false);

  const checkMaintenance = useCallback(async () => {
    try {
      const data = await api.getActiveMaintenance();
      if (data.active) {
        setIsUnderMaintenance(true);
        setMessage(data.message);
        setEndsAt(new Date(data.validTo));
      } else {
        setIsUnderMaintenance(false);
        setMessage('');
        setEndsAt(null);
      }
    } catch (err) {
      console.error('Maintenance check failed:', err);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    checkMaintenance();
    intervalRef.current = setInterval(checkMaintenance, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, interval, checkMaintenance]);

  useEffect(() => {
    if (!isUnderMaintenance || !endsAt) return;

    const now = new Date();
    const remaining = endsAt.getTime() - now.getTime();

    if (remaining <= 0) {
      setIsUnderMaintenance(false);
      setMessage('');
      setEndsAt(null);
      return;
    }

    const timer = setTimeout(() => {
      setIsUnderMaintenance(false);
      setMessage('');
      setEndsAt(null);
      checkMaintenance();
    }, remaining);

    return () => clearTimeout(timer);
  }, [isUnderMaintenance, endsAt, checkMaintenance]);

  useEffect(() => {
    if (wasUnderMaintenanceRef.current && !isUnderMaintenance) {
      const jitter = Math.floor(Math.random() * 10000);
      setTimeout(() => window.location.reload(), jitter);
    }
    wasUnderMaintenanceRef.current = isUnderMaintenance;
  }, [isUnderMaintenance]);

  return { isUnderMaintenance, message, endsAt };
}

export default useMaintenanceCheck;
